import React from 'react';

import typ from './index.d';
import {
  AttachmentActions,
  AttachmentArea,
  AttachmentName,
  AttachmentItem,
  Box,
  CloseIcon,
  Container,
  Description,
  DownloadIcon,
  LoadingIndicator,
  LoadRow,
  PdfIcon,
  Title,
  TitleArea,
  TrashIcon,
  WarnEmpty,
  ResumeInput,
  ResumeContent,
  ResumeName,
  ResumeAddButton,
  CloseSmallIcon,
  CheckIcon,
  ArrowIconRight,
  ArrowIconLeft,
  NavigationRow,
} from './styled';
import Button from 'components/button';
import { AttachmentCRUD } from 'services/requests/attachment';
import { toast } from 'react-toastify';
import { CONST_API } from 'utils/CONSTANTS';
import Cookies from 'js-cookie';

const attachmentCrud = new AttachmentCRUD()

const ManagePdfModal: React.FC<typ.Props> = ({
  userId,
  userName,
  type,
  onClose,
  onVisualizate,
}) => {
  // Ref
  const currentPage = React.useRef(1);
  const inputCurriculoRef = React.useRef<HTMLInputElement>(null);

  const [resume, setResume] = React.useState<File>();
  const [isAddPdf, setIsAddPdf] = React.useState(false);
  const [attachments, setAttachments] = React.useState<typ.attachment>();
  const [loading, setLoading] = React.useState({
    getAttachs: false,
    add: false,
    delete: false,
    download: false,
  });

  // Callbacks
  const getAttachments = React.useCallback(async ({ page } : typ.getFunction) => {
    try {
      if(!userId) throw new Error('Não foi possivel carregar selecionar o cliente. (1)');
      currentPage.current = page || 1;
      setLoading(e => ({...e, getAttachs: true}))
      
      const { data, status } = await attachmentCrud.read({ type: type, id: userId, page })

      if(status === 200 && data) {
        setAttachments(data)
        return;
      }

      throw new Error('Não foi possivel carregar os currículos')
    } catch (e) {
      const { message } = e as Error
      toast.error(message)
    } finally {
      setLoading(e => ({...e, getAttachs: false}))
    }
  }, [])

  const downloadPDF = React.useCallback(async ({ id, name }: typ.downloadFunction) => {
    try {
      if(loading.download) throw new Error ('Aguarde, o download já está acontecendo .(1)')
      if(!id) throw new Error('Não foi possivel selecionar o currículo .(0)');

      setLoading(e => ({...e, download: true}))

      const response = await fetch(`${CONST_API.protocol}${CONST_API.baseUrl}/attachment/view/${id}`, {
        method: 'GET',
        headers: {
          Accept: 'application/pdf',
          Authorization: `Bearer ${Cookies.get('token')}`
        }
      })

      if(!response.ok) {
        throw new Error('Nenhum pdf encontrado. (1)')
      }

      const pdf = await response.blob();

      const url = URL.createObjectURL(pdf);
      const link = document.createElement('a');

      link.href = url;
      link.download = `${name}`; 
      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast('Currículo baixado com sucesso.')
    } catch (e) {
      const {message} = e as Error
      toast.error(message);
    } finally {
      setLoading(e => ({...e, download: false}))
    }
  }, [loading.download]);

  const deletePdf = React.useCallback(async ({ id }: typ.deleteFunction) => {
    try {
      if(loading.delete) throw new Error ('Aguarde, algumas chamadas ainda estão acontecendo(2)')
      if(!id) throw new Error('Não foi possivel selecionar o currículo .(1)');

      setLoading(e => ({...e, delete: true}))

      const { data, status } = await attachmentCrud.delete({ id })

      if(status === 200 && data) {
        toast('Currículo deletado com sucesso.');
        getAttachments({});
        return;
      }

      throw new Error('Não foi possivel deletado o currículo')
    } catch (e) {
      const { message } = e as Error
      toast.error(message)
    } finally {
      setLoading(e => ({...e, delete: false}))
    }
  }, [loading.delete])

  const addPdf = React.useCallback(async () => {
    try {
      if(loading.add) throw new Error ('Aguarde, algumas chamadas ainda estão acontecendo. (3)')
      if(!resume) throw new Error('Não foi possivel selecionar o currículo .(2)');
      if(!userId) throw new Error('Não foi possivel carregar selecionar o cliente. (2)');
      setLoading(e => ({...e, add: true}))

      const { data, status, error } = await attachmentCrud.create({ type, id: userId, attachment: resume })

      if(status === 200 && data) {
        toast('Currículo criado com sucesso.');
        getAttachments({});
        setIsAddPdf(false);
        setResume(undefined)
        return;
      }

      throw new Error('Não foi possivel criar o currículo')
    } catch (e) {
      const { message } = e as Error
      toast.error(message)
    } finally {
      setLoading(e => ({...e, add: false}))
    }
  }, [resume, loading.add])

  // Effect
  React.useEffect(() => {
    getAttachments({})
  }, [])

  return (
    <Container>
      <Box>
        <TitleArea>
          <Title>Selecione o currículo</Title>
          <CloseIcon onClick={onClose}/>
        </TitleArea>
        <Description>{isAddPdf ? "Clique abaixo para adicionar um novo currículo": `Veja abaixo os currículos  de ${userName}`}</Description>
        {isAddPdf ? (
          <>
            <ResumeInput 
              type="file"
              accept="application/pdf"
              ref={inputCurriculoRef}
              onChange={event => {
                if (event && event.target?.files?.[0]) {
                  setResume(event.target.files[0]);
                }
              }}
            />
            <>
              {loading.add ? (
                <LoadRow>
                  <LoadingIndicator />
                </LoadRow>
              ) : (
                <>
                  {resume ? (
                    <ResumeContent>
                      <ResumeName>{resume.name}</ResumeName>
                      <CloseSmallIcon onClick={() => setResume(undefined)}/>
                      <CheckIcon onClick={() => addPdf()}/>
                    </ResumeContent>
                  ) : (
                    <ResumeAddButton onClick={() => inputCurriculoRef.current?.click()}>Clique aqui</ResumeAddButton>
                  )}
                </>
              )}
            </>
          </>
        ) : (
          <>
            { loading.getAttachs ? (
              <LoadRow>
                <LoadingIndicator />
              </LoadRow>
            ) : (
              <>
                { attachments?.Attachment.length ? (
                  <AttachmentArea>
                    {attachments.Attachment.map((e, i) => (
                      <AttachmentItem>
                        <AttachmentName>{e.name}</AttachmentName>
                        <AttachmentActions>
                          <PdfIcon onClick={() => onVisualizate?.({id: e.id})}/>
                          <DownloadIcon onClick={() => downloadPDF({id: e.id, name: e.name})}/>
                          <TrashIcon onClick={() => deletePdf( { id: e.id } )}/>
                        </AttachmentActions>
                      </AttachmentItem>
                    ))}
                    <NavigationRow>
                      <ArrowIconLeft  disabled={!attachments.has_prev} onClick={() => {
                        if(!attachments.has_prev) return;
                        getAttachments({page: currentPage.current - 1})
                      }}/>
                      <ArrowIconRight disabled={!attachments.has_next} onClick={() => {
                        if(!attachments.has_next) return;
                        getAttachments({page: currentPage.current + 1})
                      }}/>
                    </NavigationRow>
                  </AttachmentArea>
                ) : (
                  <WarnEmpty>Nenhum currículo encontrado...</WarnEmpty>
                )}
              </>
            )}
          </>
        )}

        <Button
          isLoading={loading.getAttachs || loading.add}
          title={isAddPdf ? 'Voltar aos currículos' : 'Adicionar currículo'}
          callback={() => {
            setIsAddPdf(!isAddPdf)
            setResume(undefined)
          }}
          customStyle={{ marginTop: 20 }}
        />
      </Box>
    </Container>
  );
};

export default ManagePdfModal;
